<div class="space-y-6 p-5">

  <!-- Header -->
  <div>
    <h2 class="text-sm font-semibold text-slate-900">Execution-Plane Monitoring Rules</h2>
    <p class="mt-0.5 text-xs text-slate-500 max-w-2xl">
      These settings control when the reconciliation workers and event handlers <strong>open and resolve findings</strong>.
      They do <em>not</em> affect the dashboard's historical "Issues&nbsp;(24H)" CloudWatch metrics display.
      Changes take effect on the next evaluation cycle.
    </p>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="flex items-center gap-2 text-sm text-slate-400">
    <lucide-icon name="loader" [size]="14" class="animate-spin" [strokeWidth]="2"></lucide-icon>
    Loading…
  </div>

  <!-- Error -->
  <div *ngIf="error" class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
    <lucide-icon name="triangle-alert" [size]="14" [strokeWidth]="2"></lucide-icon>
    {{ error }}
  </div>

  <!-- ── Polling Frequency ──────────────────────────────────────────────── -->
  <div class="overflow-hidden rounded-xl border border-slate-200 bg-white">
    <div class="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-5 py-3">
      <div>
        <p class="text-xs font-semibold text-slate-800">Polling Frequency</p>
        <p class="text-[10px] text-slate-500">How often the reconciliation dispatcher checks all monitored services. Changes take effect immediately.</p>
      </div>
      <button
        *ngIf="!schedulerEditing"
        (click)="startSchedulerEdit()"
        class="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
      >
        <lucide-icon name="settings-2" [size]="11" [strokeWidth]="2"></lucide-icon>
        Edit
      </button>
    </div>

    <div class="px-5 py-4">

      <!-- Error -->
      <p *ngIf="schedulerError && !schedulerEditing" class="text-xs text-red-600">{{ schedulerError }}</p>

      <!-- View mode -->
      <div *ngIf="!schedulerEditing" class="flex items-center gap-6 text-xs">
        <div>
          <p class="text-slate-500">Poll interval</p>
          <p class="mt-0.5 font-medium text-slate-800">
            {{ schedulerConfig ? schedulerConfig.frequencyMinutes + ' min' : '—' }}
          </p>
        </div>
        <div>
          <p class="text-slate-500">Schedule expression</p>
          <p class="mt-0.5 font-mono text-slate-700">{{ schedulerConfig?.expression ?? '—' }}</p>
        </div>
        <div>
          <p class="text-slate-500">State</p>
          <span class="mt-0.5 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
            [class.bg-emerald-50]="schedulerConfig?.state === 'ENABLED'"
            [class.text-emerald-700]="schedulerConfig?.state === 'ENABLED'"
            [class.bg-zinc-100]="schedulerConfig?.state !== 'ENABLED'"
            [class.text-zinc-500]="schedulerConfig?.state !== 'ENABLED'">
            {{ schedulerConfig?.state ?? '—' }}
          </span>
        </div>
      </div>

      <!-- Edit mode -->
      <div *ngIf="schedulerEditing" class="space-y-4">
        <div class="flex items-center gap-4">
          <div>
            <label class="block text-[10px] font-medium text-slate-600 mb-1">Poll interval</label>
            <select
              [(ngModel)]="schedulerDraft"
              [ngModelOptions]="{ standalone: true }"
              class="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              <option *ngFor="let opt of pollingFrequencyOptions" [ngValue]="opt.value">{{ opt.label }}</option>
            </select>
          </div>
        </div>
        <p *ngIf="schedulerError" class="text-xs text-red-600">{{ schedulerError }}</p>
        <div class="flex items-center gap-2">
          <button
            (click)="saveSchedulerConfig()"
            [disabled]="schedulerSaving"
            class="flex items-center gap-1 rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-700 disabled:opacity-60"
          >
            <lucide-icon *ngIf="schedulerSaving" name="loader" [size]="11" [strokeWidth]="2" class="animate-spin"></lucide-icon>
            {{ schedulerSaving ? 'Saving…' : 'Save' }}
          </button>
          <button
            (click)="cancelSchedulerEdit()"
            [disabled]="schedulerSaving"
            class="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </div>

    </div>
  </div>

  <ng-container *ngIf="!loading && !error && globalRule">

    <!-- ── Global defaults ──────────────────────────────────────────────────── -->
    <div class="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div class="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-5 py-3">
        <div>
          <p class="text-xs font-semibold text-slate-800">Global Defaults</p>
          <p class="text-[10px] text-slate-500">Applies to all service types unless a service-type override is active.</p>
        </div>
        <div class="flex items-center gap-3">
          <span *ngIf="globalRule.updatedBy" class="text-[10px] text-slate-400">
            Updated by {{ globalRule.updatedBy }} · {{ globalRule.updatedAt | date:'MMM d, y HH:mm' }}
          </span>
          <button
            *ngIf="!globalEditing"
            (click)="startGlobalEdit()"
            class="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            <lucide-icon name="settings-2" [size]="11" [strokeWidth]="2"></lucide-icon>
            Edit
          </button>
        </div>
      </div>

      <!-- View mode -->
      <div *ngIf="!globalEditing" class="grid grid-cols-2 gap-x-8 gap-y-3 px-5 py-4 text-xs sm:grid-cols-3 lg:grid-cols-4">
        <div>
          <p class="text-slate-500">Finding generation</p>
          <span class="mt-0.5 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
            [class.bg-emerald-50]="globalRule.enabled" [class.text-emerald-700]="globalRule.enabled"
            [class.bg-zinc-100]="!globalRule.enabled" [class.text-zinc-500]="!globalRule.enabled">
            {{ globalRule.enabled ? 'ENABLED' : 'DISABLED' }}
          </span>
        </div>
        <div>
          <p class="text-slate-500">Evaluation window</p>
          <p class="mt-0.5 font-medium text-slate-800">{{ globalRule.evaluationWindowMinutes }} min</p>
        </div>
        <div>
          <p class="text-slate-500">Metric period</p>
          <p class="mt-0.5 font-medium text-slate-800">{{ globalRule.metricPeriodSeconds }}s</p>
        </div>
        <div>
          <p class="text-slate-500">Error rate threshold</p>
          <p class="mt-0.5 font-medium text-slate-800">{{ globalRule.errorRateThresholdPercent }}%</p>
        </div>
        <div>
          <p class="text-slate-500">Min sample count</p>
          <p class="mt-0.5 font-medium text-slate-800">{{ globalRule.minimumSampleCount }} invocations</p>
        </div>
        <div>
          <p class="text-slate-500">Throttle threshold</p>
          <p class="mt-0.5 font-medium text-slate-800">{{ globalRule.throttleCountThreshold }} throttles</p>
        </div>
        <div>
          <p class="text-slate-500">HIGH severity at</p>
          <p class="mt-0.5 font-medium text-slate-800">≥ {{ globalRule.severityHighThresholdPercent }}% errors</p>
        </div>
      </div>

      <!-- Edit mode -->
      <div *ngIf="globalEditing && globalDraft" class="space-y-5 px-5 py-4">
        <ng-container *ngTemplateOutlet="ruleForm; context: { $implicit: globalDraft, isOverride: false }"></ng-container>

        <div *ngIf="globalError" class="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-700">
          {{ globalError }}
        </div>

        <div class="flex items-center gap-2 border-t border-slate-100 pt-4">
          <button
            (click)="saveGlobal()"
            [disabled]="globalSaving"
            class="flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
          >
            <lucide-icon *ngIf="globalSaving" name="loader" [size]="11" class="animate-spin" [strokeWidth]="2"></lucide-icon>
            {{ globalSaving ? 'Saving…' : 'Save global rule' }}
          </button>
          <button
            (click)="cancelGlobalEdit()"
            [disabled]="globalSaving"
            class="rounded-lg border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- ── Service-type overrides ──────────────────────────────────────────── -->
    <div>
      <p class="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Service-Type Overrides</p>
      <p class="mb-4 text-xs text-slate-500">
        Enable an override to apply different thresholds for a specific service type.
        When disabled, the service type falls back to the global defaults above.
      </p>

      <div class="space-y-6">
        <div *ngFor="let group of serviceTypeGroups">
          <p class="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
            <lucide-icon name="cloud" [size]="12" [strokeWidth]="2" class="text-slate-400"></lucide-icon>
            {{ group.label }}
          </p>
          <div class="space-y-4">
            <div
              *ngFor="let section of group.sections"
              class="overflow-hidden rounded-xl border border-slate-200 bg-white"
            >
              <div class="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-5 py-3">
            <div class="flex items-center gap-3">
              <p class="text-xs font-semibold text-slate-800">{{ section.label }}</p>
              <span
                class="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                [class.bg-violet-100]="isUsingOverride(section)"
                [class.text-violet-700]="isUsingOverride(section)"
                [class.bg-slate-100]="!isUsingOverride(section)"
                [class.text-slate-500]="!isUsingOverride(section)"
              >
                {{ isUsingOverride(section) ? 'Override active' : 'Using global' }}
              </span>
            </div>
            <div class="flex items-center gap-3">
              <span *ngIf="section.override?.updatedBy" class="text-[10px] text-slate-400">
                Updated by {{ section.override!.updatedBy }} · {{ section.override!.updatedAt | date:'MMM d, y HH:mm' }}
              </span>
              <button
                *ngIf="!section.editing"
                (click)="startOverrideEdit(section)"
                class="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                <lucide-icon name="settings-2" [size]="11" [strokeWidth]="2"></lucide-icon>
                {{ section.override ? 'Edit' : 'Add override' }}
              </button>
            </div>
          </div>

          <!-- View mode -->
          <div *ngIf="!section.editing" class="grid grid-cols-2 gap-x-8 gap-y-3 px-5 py-4 text-xs sm:grid-cols-3 lg:grid-cols-4">
            <div>
              <p class="text-slate-500">Finding generation</p>
              <span class="mt-0.5 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
                [class.bg-emerald-50]="effectiveValue(section, 'enabled')"
                [class.text-emerald-700]="effectiveValue(section, 'enabled')"
                [class.bg-zinc-100]="!effectiveValue(section, 'enabled')"
                [class.text-zinc-500]="!effectiveValue(section, 'enabled')">
                {{ effectiveValue(section, 'enabled') ? 'ENABLED' : 'DISABLED' }}
              </span>
            </div>
            <div>
              <p class="text-slate-500">Evaluation window</p>
              <p class="mt-0.5 font-medium text-slate-800">{{ effectiveValue(section, 'evaluationWindowMinutes') }} min</p>
            </div>
            <div>
              <p class="text-slate-500">Metric period</p>
              <p class="mt-0.5 font-medium text-slate-800">{{ effectiveValue(section, 'metricPeriodSeconds') }}s</p>
            </div>
            <div>
              <p class="text-slate-500">Error rate threshold</p>
              <p class="mt-0.5 font-medium text-slate-800">{{ effectiveValue(section, 'errorRateThresholdPercent') }}%</p>
            </div>
            <div>
              <p class="text-slate-500">Min sample count</p>
              <p class="mt-0.5 font-medium text-slate-800">{{ effectiveValue(section, 'minimumSampleCount') }} invocations</p>
            </div>
            <div *ngIf="section.serviceType === 'LAMBDA'">
              <p class="text-slate-500">Throttle threshold</p>
              <p class="mt-0.5 font-medium text-slate-800">{{ effectiveValue(section, 'throttleCountThreshold') }} throttles</p>
            </div>
            <div>
              <p class="text-slate-500">HIGH severity at</p>
              <p class="mt-0.5 font-medium text-slate-800">≥ {{ effectiveValue(section, 'severityHighThresholdPercent') }}% errors</p>
            </div>
          </div>

          <!-- Edit mode -->
          <div *ngIf="section.editing && section.draft" class="space-y-5 px-5 py-4">
            <!-- Override toggle -->
            <label class="flex items-center gap-2.5 text-xs">
              <input type="checkbox" [(ngModel)]="section.draft.overrideEnabled"
                class="h-3.5 w-3.5 rounded border-slate-300 text-violet-600 focus:ring-violet-400">
              <span class="font-medium text-slate-700">Enable this override</span>
              <span class="text-slate-400">(uncheck to store settings without activating the override)</span>
            </label>

            <ng-container *ngTemplateOutlet="ruleForm; context: { $implicit: section.draft, isOverride: true, serviceType: section.serviceType }"></ng-container>

            <div *ngIf="section.error" class="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-700">
              {{ section.error }}
            </div>

            <div class="flex items-center gap-2 border-t border-slate-100 pt-4">
              <button
                (click)="saveOverride(section)"
                [disabled]="section.saving"
                class="flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
              >
                <lucide-icon *ngIf="section.saving" name="loader" [size]="11" class="animate-spin" [strokeWidth]="2"></lucide-icon>
                {{ section.saving ? 'Saving…' : 'Save ' + section.label + ' override' }}
              </button>
              <button
                (click)="cancelOverrideEdit(section)"
                [disabled]="section.saving"
                class="rounded-lg border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </ng-container>
</div>

<!-- ── Shared rule form template ───────────────────────────────────────────── -->
<ng-template #ruleForm let-draft let-isOverride="isOverride" let-serviceType="serviceType">
  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">

    <!-- Enabled -->
    <div class="col-span-full">
      <label class="flex items-center gap-2.5 text-xs">
        <input type="checkbox" [(ngModel)]="draft.enabled"
          class="h-3.5 w-3.5 rounded border-slate-300 text-violet-600 focus:ring-violet-400">
        <span class="font-medium text-slate-700">Finding generation enabled</span>
        <span class="text-slate-400">(disable to suppress all findings for this scope)</span>
      </label>
    </div>

    <!-- Evaluation window -->
    <div>
      <label class="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        Evaluation window (minutes)
        <span class="ml-1 normal-case font-normal text-slate-400">How far back CloudWatch looks</span>
      </label>
      <input type="number" [(ngModel)]="draft.evaluationWindowMinutes" min="1" max="1440"
        class="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400">
    </div>

    <!-- Metric period -->
    <div>
      <label class="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        Metric period
        <span class="ml-1 normal-case font-normal text-slate-400">CloudWatch sampling granularity</span>
      </label>
      <select [(ngModel)]="draft.metricPeriodSeconds"
        class="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400">
        <option *ngFor="let opt of metricPeriodOptions" [value]="opt.value">{{ opt.label }}</option>
      </select>
    </div>

    <!-- Error rate threshold -->
    <div>
      <label class="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        Error rate threshold (%)
        <span class="ml-1 normal-case font-normal text-slate-400">Opens a MEDIUM or HIGH finding</span>
      </label>
      <input type="number" [(ngModel)]="draft.errorRateThresholdPercent" min="0.1" max="100" step="0.1"
        class="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400">
    </div>

    <!-- Min sample count -->
    <div>
      <label class="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        Minimum sample count
        <span class="ml-1 normal-case font-normal text-slate-400">Min invocations before evaluating</span>
      </label>
      <input type="number" [(ngModel)]="draft.minimumSampleCount" min="1" max="10000"
        class="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400">
    </div>

    <!-- Throttle count threshold (Lambda-specific) -->
    <div *ngIf="!isOverride || serviceType === 'LAMBDA' || !serviceType">
      <label class="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        Throttle count threshold
        <span class="ml-1 normal-case font-normal text-slate-400">Absolute throttles (Lambda only)</span>
      </label>
      <input type="number" [(ngModel)]="draft.throttleCountThreshold" min="1"
        class="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400">
    </div>

    <!-- HIGH severity threshold -->
    <div>
      <label class="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        HIGH severity threshold (%)
        <span class="ml-1 normal-case font-normal text-slate-400">Error rate that escalates to HIGH</span>
      </label>
      <input type="number" [(ngModel)]="draft.severityHighThresholdPercent" min="0.1" max="100" step="0.1"
        class="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400">
    </div>

  </div>

  <!-- UX note -->
  <div class="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-[11px] text-amber-700">
    <strong>Note:</strong> These settings control finding generation in the execution plane.
    They do <em>not</em> change the dashboard's historical "Issues&nbsp;(24H)" CloudWatch metrics display.
    Recovery is automatic: the first clean evaluation cycle resolves open findings.
  </div>
</ng-template>
